import React, { useState } from 'react';
import { CheckCircle, Users, Shirt, AlertCircle, Loader2 } from 'lucide-react';

/**
 * JerseySelector Component
 * 
 * Displays detected jersey colors and allows coach to select which team to analyze.
 * This solves Drew's question: "How do we know which is Team A and which is Team B?"
 * 
 * Props:
 * - jerseys: Array of detected jersey objects [{team_id, hex, color_name, player_count, label}]
 * - onSelect: Callback when coach selects a team (team_id, teamLabel)
 * - onCancel: Callback to go back and upload different video
 * - loading: Boolean for loading state during analysis
 * - videoInfo: Object with video details {filename, gcsPath}
 */
const JerseySelector = ({ 
  jerseys = [], 
  onSelect, 
  onCancel, 
  loading = false,
  videoInfo = {}
}) => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [customLabel, setCustomLabel] = useState('');
  const [showLabelInput, setShowLabelInput] = useState(false);

  const handleTeamClick = (jersey) => {
    setSelectedTeam(jersey.team_id);
    setShowLabelInput(true);
    // Pre-fill with color-based label
    setCustomLabel('');
  };

  const handleConfirm = () => {
    if (selectedTeam === null) return;
    
    const selectedJersey = jerseys.find(j => j.team_id === selectedTeam);
    const teamLabel = customLabel.trim() || selectedJersey?.label || `Team ${selectedTeam}`;
    
    onSelect(selectedTeam, teamLabel, selectedJersey?.hex);
  };

  // If no jerseys detected, show error state
  if (!jerseys || jerseys.length < 2) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Could Not Detect Teams
          </h2>
          <p className="text-gray-600 mb-6">
            We couldn't identify distinct jersey colors in this video. 
            Please upload clearer match footage with visible players.
          </p>
          
          <button
            onClick={onCancel}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            Try Different Video
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <Shirt className="h-8 w-8 text-green-600" />
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Teams Detected!
        </h2>
        <p className="text-gray-600">
          We found {jerseys.length} team jerseys in your video. 
          <br />
          <strong>Select which team you want to analyze:</strong>
        </p>
      </div>

      {/* Video Info */}
      {videoInfo.filename && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm text-gray-600">
          <strong>Video:</strong> {videoInfo.filename}
        </div>
      )}

      {/* Jersey Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {jerseys.map((jersey) => (
          <JerseyCard
            key={jersey.team_id}
            jersey={jersey}
            isSelected={selectedTeam === jersey.team_id}
            onClick={() => handleTeamClick(jersey)}
            disabled={loading}
          />
        ))}
      </div>

      {/* Custom Label Input (shown after selection) */}
      {showLabelInput && selectedTeam !== null && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <strong>Optional:</strong> Name this team (e.g., "Al Nassr", "Al Hilal")
          </label>
          <input
            type="text"
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            placeholder={jerseys.find(j => j.team_id === selectedTeam)?.label || "Enter team name"}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            disabled={loading}
          />
          <p className="text-xs text-gray-500 mt-1">
            This name will appear in your analysis report instead of "Team A"
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Upload Different Video
        </button>
        
        <button
          onClick={handleConfirm}
          disabled={selectedTeam === null || loading}
          className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
            selectedTeam === null || loading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <CheckCircle className="h-5 w-5" />
              <span>Analyze Selected Team</span>
            </>
          )}
        </button>
      </div>

      {/* Help Text */}
      <p className="text-center text-sm text-gray-500 mt-6">
        💡 <strong>Tip:</strong> Select the opponent team you want tactical analysis on. 
        Our AI will focus only on their formation, movements, and weaknesses.
      </p>
    </div>
  );
};

/**
 * JerseyCard Component
 * Individual jersey color card with selection state
 */
const JerseyCard = ({ jersey, isSelected, onClick, disabled }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative w-full p-6 rounded-xl border-2 transition-all text-left ${
        isSelected
          ? 'border-blue-600 bg-blue-50 shadow-lg'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-3 right-3">
          <CheckCircle className="h-6 w-6 text-blue-600" />
        </div>
      )}

      {/* Jersey Color Preview */}
      <div className="flex items-center gap-4 mb-4">
        <div
          className="w-16 h-20 rounded-lg shadow-inner border-2 border-gray-200"
          style={{
            backgroundColor: jersey.hex,
            // Add jersey-like appearance
            backgroundImage: `linear-gradient(180deg, 
              rgba(255,255,255,0.2) 0%, 
              rgba(0,0,0,0.1) 100%)`
          }}
        />
        
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            {jersey.color_name} Jersey
          </h3>
          <p className="text-sm text-gray-600">
            {jersey.hex}
          </p>
        </div>
      </div>

      {/* Player Count */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Users className="h-4 w-4" />
        <span>
          <strong>{jersey.player_count}</strong> players detected
        </span>
      </div>

      {/* Hover/Selected State Label */}
      <div className={`mt-4 text-center py-2 rounded-lg text-sm font-medium ${
        isSelected
          ? 'bg-blue-600 text-white'
          : 'bg-gray-100 text-gray-600'
      }`}>
        {isSelected ? '✓ Selected for Analysis' : 'Click to Select'}
      </div>
    </button>
  );
};

export default JerseySelector;
